/**
 * NotificationBell Component
 *
 * Bell icon button with unread badge and dropdown functionality.
 * Manages notification polling and dropdown visibility.
 */

import React, { useState, useCallback } from 'react';
import {
    IconButton,
    Badge,
    Tooltip,
} from '@mui/material';
import { NotificationsNone as NotificationsIcon } from '@mui/icons-material';
import NotificationDropdown from './NotificationDropdown';
import { useNotificationPolling } from '../../hooks/useNotificationPolling';

const NotificationBell = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const {
        notifications,
        unreadCount,
        loading,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification,
    } = useNotificationPolling(true);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleFetchNotifications = useCallback((page, category) => {
        fetchNotifications(page, category);
    }, [fetchNotifications]);

    return (
        <>
            <Tooltip title="Notifications">
                <IconButton
                    onClick={handleClick}
                    size="small"
                    sx={{
                        ml: 1,
                        transition: 'all 0.3s',
                        '&:hover': {
                            transform: 'scale(1.05)',
                            bgcolor: 'rgba(0, 0, 0, 0.04)',
                        },
                    }}
                    aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
                    aria-controls={open ? 'notification-dropdown' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                >
                    <Badge
                        badgeContent={unreadCount > 99 ? '99+' : unreadCount}
                        color="error"
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: '0.65rem',
                                height: 18,
                                minWidth: 18,
                                fontWeight: 600,
                            },
                        }}
                    >
                        <NotificationsIcon sx={{ color: '#5f6368', fontSize: 24 }} />
                    </Badge>
                </IconButton>
            </Tooltip>

            <NotificationDropdown
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                notifications={notifications}
                unreadCount={unreadCount}
                loading={loading}
                onFetchNotifications={handleFetchNotifications}
                onMarkAsRead={markAsRead}
                onMarkAllAsRead={markAllAsRead}
                onDelete={deleteNotification}
            />
        </>
    );
};

export default NotificationBell;
